import { useEffect, useState } from "react";

import { HandRaisedIcon } from "@heroicons/react/24/outline";

import MatchedVehicles from "./MatchedVehicles";
import ConfirmModal from "../shared/Confirm/ConfirmModal";
import SearchPaletteInput, { SearchPaletteItem } from "../shared/Inputs/SearchPaletteInput";
import { IClaimPartRequest, IPartRequest, PartRequestSource } from "../../models/partRequests/IPartRequest";
import { partRequestVehicleLabel } from "../../utils/partRequests";

interface ClaimPartRequestModalProps {
  open: boolean;
  setOpen: any;
  request: IPartRequest | undefined;
  onConfirm: (claim: IClaimPartRequest) => void;
  isLoading: boolean;
}

export default function ClaimPartRequestModal({ open, setOpen, request, onConfirm, isLoading }: ClaimPartRequestModalProps) {
  const [message, setMessage] = useState("");
  const [vehicleId, setVehicleId] = useState<string | null>(null);
  const [part, setPart] = useState<SearchPaletteItem | null>(null);

  useEffect(() => {
    if (!open) return;
    setMessage("");
    setPart(null);
    // Pre-select the strongest match so the common case is one click.
    setVehicleId(request?.matchedVehicles[0]?.vehicleId ?? null);
  }, [open, request?.id]);

  const isPartPro = request?.source === PartRequestSource.PartPro;
  const vehicle = request ? partRequestVehicleLabel(request) : "";

  const modalMessage = isPartPro
    ? `Confirm to the buyer that you can supply "${request?.partWanted}" for a ${vehicle}. ` +
      `PartPro will send them your message and unlock their contact details here. ` +
      `Other yards may also confirm the same request - the buyer chooses who to deal with.`
    : `Mark this request as being handled by you. The customer's details are already on the request.`;

  return (
    <ConfirmModal
      open={open}
      setOpen={setOpen}
      title={isPartPro ? "Claim this PartPro request?" : "Claim this request?"}
      message={modalMessage}
      confirmButtonText={isPartPro ? "Confirm to buyer" : "Claim"}
      onConfirm={() =>
        onConfirm({
          message: message.trim() || null,
          vehicleId,
          partId: part ? String(part.id) : null,
        })
      }
      isLoading={isLoading}
      confirmDisabled={isPartPro && message.trim().length === 0}
      Icon={<HandRaisedIcon className="h-6 w-6 text-primary-600" aria-hidden="true" />}
      iconBg="bg-primary-100"
    >
      <div className="mt-4 space-y-4 text-left">
        <div>
          <label className="text-sm font-medium text-gray-500">
            {isPartPro ? "Message to the buyer" : "Note"}
            {isPartPro && <span className="text-primary-500">*</span>}
          </label>
          <textarea
            className="mt-1 w-full rounded-md border-gray-300 text-sm text-gray-900 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
            rows={4}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder={isPartPro ? "Hi, we have this part in stock. It's £.. plus delivery, tested and with a warranty." : ""}
          />
        </div>

        {request && request.matchedVehicles.length > 0 && (
          <div>
            <label className="text-sm font-medium text-gray-500">Vehicle it's coming from (optional)</label>
            <div className="mt-1">
              <MatchedVehicles
                matches={request.matchedVehicles}
                selectedVehicleId={vehicleId}
                onSelect={(match) => setVehicleId(vehicleId === match.vehicleId ? null : match.vehicleId)}
              />
            </div>
          </div>
        )}

        <div>
          <SearchPaletteInput
            label="Stock item (optional)"
            placeholder={part?.name ?? "Search stock by tag number or title"}
            type="Stock"
            onSelect={setPart}
          />
          {isPartPro && (
            <p className="mt-1 text-xs text-gray-500">
              If the part is listed on PartPro the buyer is sent straight to the listing.
            </p>
          )}
        </div>
      </div>
    </ConfirmModal>
  );
}
